import type { TFunction } from 'i18next';
import type { BulkItemResult, BulkSendResult } from '@/types/approval';

/** Stable API reason codes a bulk send / release can skip an item with -> locale key (common). */
const REASON_KEYS: Record<string, string> = {
  'release.frozen_in_review': 'release_bulk_reason_frozen_in_review',
  'release.not_creator': 'release_bulk_reason_not_creator',
  'release.not_in_review': 'release_bulk_reason_not_in_review',
  'release.no_access': 'release_bulk_reason_no_access',
  'release.not_found': 'release_bulk_reason_not_found',
  'release.item_not_found': 'release_bulk_reason_not_found',
  'release.already_released': 'release_bulk_reason_already_released',
  'release.already_in_review': 'release_bulk_reason_already_in_review',
  'release.empty_content': 'release_bulk_reason_empty_content',
  'release.failed': 'release_bulk_reason_failed',
  'release.frozen_released': 'release_bulk_reason_frozen_released',
  'release.frozen_committed': 'release_bulk_reason_frozen_committed',
  'release.not_seed_owner': 'release_bulk_reason_not_seed_owner',
};

/** Localised reason for one skipped item; unknown / missing codes fall back to a generic text. */
export const bulkReasonText = (t: TFunction, reasonCode?: string): string =>
  t(reasonCode && REASON_KEYS[reasonCode] ? REASON_KEYS[reasonCode] : 'release_bulk_reason_other');

export const skippedItems = (result: BulkSendResult): BulkItemResult[] =>
  result.results.filter((r) => r.outcome === 'skipped');

/**
 * One-line summary, e.g. "3 deleted · 1 skipped" / "8 sent for review · 2 skipped" / "8 released · 2 skipped".
 * Parts with a zero count are left out.
 */
export const buildBulkSummary = (
  t: TFunction,
  kind: 'sent' | 'released' | 'deleted',
  counts: { done: number; skipped: number }
): string => {
  const parts: string[] = [];
  if (counts.done > 0) {
    parts.push(
      kind === 'sent'
        ? t('release_bulk_sent', { count: counts.done })
        : kind === 'deleted'
          ? t('release_bulk_deleted', { count: counts.done })
          : t('release_undo_bulk_released', { count: counts.done })
    );
  }
  if (counts.skipped > 0) parts.push(t('release_bulk_skipped', { count: counts.skipped }));
  return parts.join(' · ');
};
